import { useEffect, useRef, useState } from 'react';
import { GOOGLE_MAPS_MAP_ID } from '../config/googleMaps';
import { ensureGoogleMapsLoaded } from '../utils/googleMapsLoader';

const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const useAddressMapModal = ({
  formData,
  infoLines,
  hasMapCoordinates,
  openInMapsLabel,
  shouldPrepareMap
}) => {
  const [showMapModal, setShowMapModal] = useState(false);
  const [pendingMapOpen, setPendingMapOpen] = useState(false);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (!showMapModal || !hasMapCoordinates || !mapContainerRef.current || !isGoogleMapsReady) {
      return undefined;
    }

    let cancelled = false;

    const initMap = async () => {
      const { Map: GoogleMap, InfoWindow } = await window.google.maps.importLibrary('maps');
      const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary('marker');

      if (cancelled || !mapContainerRef.current) {
        return;
      }

      const position = {
        lat: Number(formData.latitude),
        lng: Number(formData.longitude)
      };

      const map = new GoogleMap(mapContainerRef.current, {
        center: position,
        zoom: 15,
        mapId: GOOGLE_MAPS_MAP_ID
      });
      const infoWindow = new InfoWindow();
      const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(
        `${formData.latitude},${formData.longitude}`
      )}`;

      const pin = new PinElement({
        borderColor: 'black',
        background: '#4087AD',
        glyphColor: 'white'
      });

      const marker = new AdvancedMarkerElement({
        map,
        position,
        content: pin.element
      });

      marker.addListener('click', () => {
        infoWindow.close();
        infoWindow.setContent(`
          <div class="ns-address-lookup__map-info">
            <div class="ns-address-lookup__map-info-address">
              ${infoLines.map((line) => `<div>${escapeHtml(line)}</div>`).join('')}
            </div>
            <a
              class="ns-address-lookup__map-info-link"
              href="${mapUrl}"
              target="_blank"
              rel="noopener noreferrer"
              title="${escapeHtml(openInMapsLabel)}"
              aria-label="${escapeHtml(openInMapsLabel)}"
            >
              ${escapeHtml(openInMapsLabel)}
            </a>
          </div>
        `);
        infoWindow.open({
          anchor: marker,
          map
        });
      });
    };

    initMap();

    return () => {
      cancelled = true;
    };
  }, [formData, hasMapCoordinates, infoLines, isGoogleMapsReady, openInMapsLabel, showMapModal]);

  useEffect(() => {
    if (!shouldPrepareMap || isGoogleMapsReady) {
      return undefined;
    }

    let cancelled = false;

    const loadGoogleMaps = async () => {
      try {
        await ensureGoogleMapsLoaded();

        if (!cancelled) {
          setIsGoogleMapsReady(true);
        }
      } catch {
        if (!cancelled) {
          setIsGoogleMapsReady(false);
        }
      }
    };

    loadGoogleMaps();

    return () => {
      cancelled = true;
    };
  }, [isGoogleMapsReady, shouldPrepareMap]);

  useEffect(() => {
    if (!showMapModal) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowMapModal(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMapModal]);

  return {
    isGoogleMapsReady,
    mapContainerRef,
    pendingMapOpen,
    setPendingMapOpen,
    setShowMapModal,
    showMapModal
  };
};

export default useAddressMapModal;
